import React, { useState, useEffect, useCallback } from 'react';
import Api from 'utils/api';
import styles from './ProductListTab.module.css';
import PageComponent from 'components/common/Page/PageComponent';

const ProductListTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [masterData, setMasterData] = useState({
    types: [],
    units: [],
    brands: []
  });

  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [modalMode, setModalMode] = useState('add');
  const [activeLang, setActiveLang] = useState('ko');

  const LANGUAGES = [
    { code: 'ko', label: '한국어' },
    { code: 'en', label: 'English' },
    { code: 'ja', label: '日本語' },
    { code: 'zh', label: '简体中文' }
  ];

  // 이미지 업로드 로직 (언어 구분 없이 공통 필드 사용)
  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const urlFieldName = `${fieldName}Url`; // 미리보기용 state 필드명

    try {
      const data = await Api.get(`/image/presignedUrl?fileName=${encodeURIComponent(file.name)}`);

      if (data.putPresignedUrl) {
        const uploadRes = await fetch(data.putPresignedUrl, {
          method: 'PUT',
          body: file,
        });

        if (uploadRes.ok) {
          setFormData(prev => ({
            ...prev,
            [fieldName]: data.s3FileName,    // API 전송용 (e.g. productImage)
            [urlFieldName]: data.getPresignedUrl // 미리보기용 (e.g. productImageUrl)
          }));
        } else {
          alert(`이미지 업로드 실패`);
        }
      }
    } catch (err) {
      console.error("업로드 중 오류:", err);
      alert("이미지 서버 통신 중 오류 발생");
    }
  };

  const fetchData = useCallback(async (page, size) => {
    setLoading(true);
    const data = await Api.get(`/product?pageNumber=${page}&pageSize=${size}`);
    if (data && data.productList) {
      const { content, totalElements, totalPages, number, size: resSize } = data.productList;
      setProducts(content || []);
      setPageInfo({
        pageNumber: number,
        pageSize: resSize,
        totalElements: totalElements,
        totalPages: totalPages
      });
    }
    setLoading(false);
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [typRes, unitRes, brandRes] = await Promise.all([
        Api.get('/product/type'),
        Api.get('/product/unit'),
        Api.get('/product/brand')
      ]);

      setMasterData({
        types: typRes.productTypeList || [],
        units: unitRes.productUnitList || [],
        brands: brandRes.productBrandList || []
      });
    } catch (err) {
      console.error("마스터 데이터 로딩 실패:", err);
    }
  }, []);

  useEffect(() => {
    fetchData(pageInfo.pageNumber, pageInfo.pageSize);
    fetchMasterData();
  }, [fetchData, fetchMasterData, pageInfo.pageNumber, pageInfo.pageSize]);

  const handleOpenModal = (mode, data = null) => {
    setModalMode(mode);
    setIsEditMode(mode === 'add');
    setActiveLang('ko');

    if (data) {
      setFormData({
        ...data,
        isForAdultFlag: Number(data.isForAdultFlag ?? 0)
      });
    } else {
      setFormData({
        productUnitId: '',
        productTypeId: '',
        productBrandId: '',
        productPrice: '',
        isForAdultFlag: 0,
        productImage: '',
        productDetailImage: '',
        productImageUrl: '',
        productDetailImageUrl: '',
        // 다국어 텍스트 필드 초기화
        productNameKo: '', productNoteKo: '',
        productNameEn: '', productNoteEn: '',
        productNameJa: '', productNoteJa: '',
        productNameZh: '', productNoteZh: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = ['productUnitId', 'productTypeId', 'productBrandId', 'productPrice', 'isForAdultFlag', 'productId'];

    let finalValue;
    if (type === 'checkbox') {
      // 체크박스면 true -> 1, false -> 0
      finalValue = checked ? 1 : 0;
    } else if (numericFields.includes(name)) {
      // 숫자 필드면 Number로 변환
      finalValue = value === "" ? "" : Number(value);
    } else {
      finalValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleSave = async () => {
    // 필수 체크 (상품명 Ko, 필수 ID값들, 이미지)
    if (!formData.productNameKo || !formData.productUnitId || !formData.productTypeId ||
      !formData.productBrandId || !formData.productImage || !formData.productDetailImage) {
      return alert("필수 정보(상품명, 분류, 이미지 등)를 모두 입력해주세요.");
    }

    try {
      let response;
      if (modalMode === 'add') {
        response = await Api.post('/product', formData);
      } else {
        response = await Api.patch('/product', formData);
      }

      if (!response) return;
      alert(modalMode === 'add' ? '등록되었습니다.' : '수정되었습니다.');
      setIsModalOpen(false);
      fetchData(pageInfo.pageNumber, pageInfo.pageSize);
    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 이 상품을 삭제하시겠습니까?")) return;
    const data = await Api.delete(`/product?productId=${formData.productId}`);
    if (data.code === 200) {
      alert("삭제가 완료되었습니다.");
      setIsModalOpen(false);
      fetchData(pageInfo.pageNumber, pageInfo.pageSize);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pageInfo.totalPages) {
      setPageInfo(prev => ({ ...prev, pageNumber: newPage }));
      window.scrollTo(0, 0);
    }
  };

  if (loading && products.length === 0) {
    return <div className={styles.container}>데이터 로딩 중...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>
          전체 상품 <span className={styles.countNumber}>{pageInfo.totalElements}</span>개
        </div>
        <button className={styles.addBtn} onClick={() => handleOpenModal('add')}>+ 상품 등록</button>
      </div>

      <div className={styles.cardGrid}>
        {products.length > 0 ? (
          products.map((m) => (
            <div key={m.productId} className={styles.productCard}>
              <div className={styles.cardMainBody}>
                <div className={styles.cardImageContainer}>
                  {m.productImageUrl ? (
                    <img src={m.productImageUrl} alt={m.productNameKo} className={styles.productThumbnail} />
                  ) : (
                    <div className={styles.noImage}>No Image</div>
                  )}
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.cardHeader}>
                    <div className={styles.productNameRow}>
                      <h3 className={styles.productName}>{m.productNameKo || '이름 없음'}</h3>
                      {Number(m.isForAdultFlag) === 1 && <span className={styles.adultBadge}>성인</span>}
                    </div>
                    <div className={styles.productInfo}><label>분류</label><span>{m.productTypeName}</span></div>
                    <div className={styles.productInfo}><label>단위</label><span>{m.productUnitName}</span></div>
                    <div className={styles.productInfo}><label>브랜드</label><span>{m.productBrandName}</span></div>
                    <div className={styles.priceText}>{m.productPrice?.toLocaleString()} 원</div>
                  </div>
                </div>
              </div>
              <div className={styles.cardFooter}>
                <button className={styles.detailBtn} onClick={() => handleOpenModal('detail', m)}>자세히</button>
              </div>
            </div>
          ))
        ) : <div className={styles.emptyMsg}>조회된 상품이 없습니다.</div>}
      </div>

      {pageInfo.totalPages > 0 && <PageComponent pageInfo={pageInfo} onPageChange={handlePageChange} />}

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{modalMode === 'add' ? '새 상품 등록' : (isEditMode ? '상품 정보 수정' : '상품 상세 정보')}</h3>
              <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>✕</button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formContainer}>
                {/* 1. 공통 정보 영역 (이미지 포함) */}
                <div className={styles.commonFields}>
                  {/* 텍스트 정보 정렬 (2열 그리드) */}
                  <div className={styles.infoGrid}>
                    <div className={styles.inputGroup}>
                      <label>분류*</label>
                      <select name="productTypeId" value={formData.productTypeId || ''} onChange={handleInputChange} disabled={!isEditMode}>
                        <option value="">선택</option>
                        {masterData.types.map(f => <option key={f.productTypeId} value={f.productTypeId}>{f.productTypeNameKo}</option>)}
                      </select>
                    </div>
                    <div className={styles.inputGroup}>
                      <label>단위*</label>
                      <select name="productUnitId" value={formData.productUnitId || ''} onChange={handleInputChange} disabled={!isEditMode}>
                        <option value="">선택</option>
                        {masterData.units.map(t => <option key={t.productUnitId} value={t.productUnitId}>{t.productUnitName}</option>)}
                      </select>
                    </div>
                    <div className={styles.inputGroup}>
                      <label>브랜드*</label>
                      <select name="productBrandId" value={formData.productBrandId || ''} onChange={handleInputChange} disabled={!isEditMode}>
                        <option value="">선택</option>
                        {masterData.brands.map(i => <option key={i.productBrandId} value={i.productBrandId}>{i.productBrandName}</option>)}
                      </select>
                    </div>
                    <div className={styles.inputGroup}>
                      <label>가격*</label>
                      <input type="number" name="productPrice" value={formData.productPrice || ''} onChange={handleInputChange} disabled={!isEditMode} placeholder="0" />
                    </div>

                    <div className={styles.inputGroup}>
                      <label className={`${styles.checkboxLabel} ${!isEditMode ? styles.checkboxReadOnly : ''}`}>
                        <div className={styles.checkboxWrapper}>
                          <input
                            type="checkbox"
                            name="isForAdultFlag"
                            // value가 1이면 체크된 것으로 표시
                            checked={formData.isForAdultFlag === 1}
                            onChange={handleInputChange}
                            disabled={!isEditMode}
                            className={styles.hiddenCheckbox}
                          />
                          {/* UI상 체크 표시 (1일 때 checked 스타일 적용) */}
                          <span className={`${styles.customCheckbox} ${formData.isForAdultFlag === 1 ? styles.checked : ''}`}>
                            {formData.isForAdultFlag === 1 && '✓'}
                          </span>
                        </div>
                        <span className={styles.labelText}>성인 인증 필요*</span>
                      </label>
                    </div>
                  </div>

                  <br />

                  {/* 이미지 영역: 대표 이미지 아래에 상세 이미지 배치 */}
                  <div className={styles.imageSection}>
                    <div className={styles.inputGroup}>
                      <label>대표 이미지*</label>
                      <div className={styles.imageUploadWrapper}>
                        <label className={styles.imageUploadLabel}>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'productImage')} disabled={!isEditMode} hidden />
                          <img src={formData.productImageUrl || '/upload_image.png'} className={styles.previewImg} alt='대표' />
                        </label>
                      </div>
                    </div>

                    <div className={styles.inputGroup}>
                      <label>상품 상세 이미지*</label>
                      <div className={styles.imageUploadWrapper}>
                        <label className={styles.imageUploadLabelDetail}>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'productDetailImage')} disabled={!isEditMode} hidden />
                          <img src={formData.productDetailImageUrl || '/upload_image.png'} className={styles.previewDetailImg} alt='상세' />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className={styles.divider} />

                {/* 2. 다국어 정보 탭 (텍스트만 입력) */}
                <div className={styles.langTabContainer}>
                  <div className={styles.tabHeader}>
                    {LANGUAGES.map(lang => (
                      <button key={lang.code} className={`${styles.tabBtn} ${activeLang === lang.code ? styles.activeTab : ''}`} onClick={() => setActiveLang(lang.code)}>
                        {lang.label}
                      </button>
                    ))}
                  </div>

                  <div className={styles.tabContent}>
                    <div className={styles.inputGroup}>
                      <label>상품 이름({activeLang.toUpperCase()})*</label>
                      <input
                        name={`productName${activeLang.charAt(0).toUpperCase() + activeLang.slice(1)}`}
                        value={formData[`productName${activeLang.charAt(0).toUpperCase() + activeLang.slice(1)}`] || ''}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        placeholder="이름 입력"
                      />
                    </div>
                    <div className={styles.inputGroup}>
                      <label>특이사항({activeLang.toUpperCase()})</label>
                      <input
                        name={`productNote${activeLang.charAt(0).toUpperCase() + activeLang.slice(1)}`}
                        value={formData[`productNote${activeLang.charAt(0).toUpperCase() + activeLang.slice(1)}`] || ''}
                        onChange={handleInputChange}
                        disabled={!isEditMode}
                        placeholder="특이사항 입력"

                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              {modalMode === 'add' ? (
                <>
                  <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>취소</button>
                  <button className={styles.saveBtn} onClick={handleSave}>등록하기</button>
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <>
                      <button className={styles.cancelBtn} onClick={() => setIsEditMode(false)}>취소</button>
                      <button className={styles.saveBtn} onClick={handleSave}>수정 완료</button>
                    </>
                  ) : (
                    <>
                      <button className={styles.editBtn} onClick={() => setIsEditMode(true)}>수정</button>
                      <button className={styles.deleteBtn} onClick={handleDelete}>삭제</button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductListTab;